'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { HttpError } from '@/lib/api/httpClient';
import {
  type CreatePriceAlertDto,
  targetValueFromForm,
  type UpdatePriceAlertDto,
  useCreatePriceAlert,
  usePriceAlerts,
  useUpdatePriceAlert,
} from '@/lib/api/priceAlerts';
import type { Market, MarketItem } from '@/lib/types/markets/market';
import { Icon } from '@/lib/ui-kit/icons';
import { Text } from '@/lib/ui-kit/text';
import { ACTIVE_ALERTS_MAX, Z_OVERLAY_MAX } from './constants';
import { PriceAlertsCreateView } from './create/PriceAlertsCreateView';
import { type EditPayload, PriceAlertEditView } from './edit/PriceAlertEditView';
import { PriceAlertsListView } from './PriceAlertsListView';
import type {
  PriceAlert,
  PriceAlertCondition,
  PriceAlertExpires,
  PriceAlertRepeat,
  PriceAlertSide,
} from './types';

type ViewMode = 'list' | 'create' | 'edit';

interface PriceAlertsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  market: Market;
  outcomes: MarketItem[];
  defaultOutcomeId?: string;
}

export function PriceAlertsDialog({
  open,
  onOpenChange,
  market,
  outcomes,
  defaultOutcomeId,
}: PriceAlertsDialogProps) {
  const t = useTranslations('priceAlerts');
  const tCommon = useTranslations('common');
  const [view, setView] = useState<ViewMode>('list');
  const [editingAlert, setEditingAlert] = useState<PriceAlert | null>(null);

  const alertsQuery = usePriceAlerts({ enabled: open });
  const createMutation = useCreatePriceAlert();
  const updateMutation = useUpdatePriceAlert();

  const activeAlertsCount = (alertsQuery.data ?? []).filter((a) => a.status === 'ACTIVE').length;
  const showCounter = view === 'list' && alertsQuery.isSuccess;

  const handleOverlayAnimationEnd = useCallback(
    (e: React.AnimationEvent<HTMLDivElement>) => {
      if (!open && e.target === e.currentTarget) {
        setView('list');
        setEditingAlert(null);
      }
    },
    [open],
  );

  const handleCreate = (payload: {
    outcomeId: string;
    side: PriceAlertSide;
    condition: PriceAlertCondition;
    targetCents: number;
    repeat: PriceAlertRepeat;
    expires: PriceAlertExpires;
    note: string;
  }) => {
    const dto: CreatePriceAlertDto = {
      eventId: market.id,
      outcomeId: payload.outcomeId,
      side: payload.side,
      conditionType: payload.condition,
      targetValue: targetValueFromForm(payload.targetCents),
      repeatMode: payload.repeat,
      expiresMode: payload.expires,
      note: payload.note.trim() || undefined,
    };

    createMutation.mutate(dto, {
      onSuccess: () => setView('list'),
      onError: (err) => {
        if (err instanceof HttpError && err.status === 409) {
          toast.error(t('errors.limitReached'));
        }
      },
    });
  };

  const handleBackToList = useCallback(() => {
    setView('list');
    setEditingAlert(null);
  }, []);
  const handleOpenCreate = useCallback(() => setView('create'), []);
  const handleOpenEdit = useCallback((alert: PriceAlert) => {
    setEditingAlert(alert);
    setView('edit');
  }, []);
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleEditSave = (payload: EditPayload) => {
    if (!editingAlert) return;
    const body: UpdatePriceAlertDto = {};
    if (payload.targetCents != null) body.targetValue = targetValueFromForm(payload.targetCents);
    if (payload.repeat) body.repeatMode = payload.repeat;
    if (payload.expires) body.expiresMode = payload.expires;
    if (payload.note != null) body.note = payload.note;

    updateMutation.mutate(
      { alertId: editingAlert.id, body },
      {
        onSuccess: () => {
          toast.success(t('edit.success'));
          handleBackToList();
        },
        onError: (err) => {
          if (err instanceof HttpError && err.status === 409) {
            toast.error(t('errors.editTerminalState'));
          }
        },
      },
    );
  };

  const isListUpdating = alertsQuery.isFetching && !alertsQuery.isLoading;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          onAnimationEnd={handleOverlayAnimationEnd}
          style={{ zIndex: Z_OVERLAY_MAX }}
          className="fixed inset-0 bg-black/70 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          style={{ zIndex: Z_OVERLAY_MAX }}
          className={[
            'fixed flex flex-col gap-6 overflow-hidden bg-v2-surface-primary px-4 py-6 shadow-none outline-none',
            'data-[state=closed]:animate-out data-[state=open]:animate-in',
            // Mobile/tablet (<800px): bottom sheet — full width
            'max-[799px]:inset-x-0 max-[799px]:bottom-0 max-[799px]:border-t max-[799px]:border-v2-border',
            // Phone (<600px): 100% height
            'max-[599px]:h-dvh',
            // Tablet (600–799px): 70% height
            'min-[600px]:max-[799px]:h-[70dvh] min-[600px]:max-[799px]:min-h-[345px]',
            'max-[799px]:data-[state=closed]:slide-out-to-bottom',
            'max-[799px]:data-[state=open]:slide-in-from-bottom',
            // Desktop (≥800px): right-side sheet — 700px wide, full height
            'min-[800px]:inset-y-0 min-[800px]:right-0 min-[800px]:h-dvh min-[800px]:w-[700px] min-[800px]:max-w-[calc(100vw-32px)] min-[800px]:px-6 min-[800px]:py-8',
            'min-[800px]:border-l min-[800px]:border-v2-border',
            'min-[800px]:data-[state=closed]:slide-out-to-right min-[800px]:data-[state=open]:slide-in-from-right',
          ].join(' ')}
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2.5">
              {(view === 'create' || view === 'edit') && (
                <SheetIconButton onClick={handleBackToList} ariaLabel={tCommon('back')}>
                  <Icon name="ArrowSmallLeft" size={24} />
                </SheetIconButton>
              )}
              <DialogPrimitive.Title asChild>
                <Text
                  as="h2"
                  variant="title-md"
                  className="flex flex-1 items-baseline gap-2 text-left font-semibold text-v2-text-primary"
                >
                  <Text as="span">
                    {view === 'create'
                      ? t('createAlert')
                      : view === 'edit'
                        ? t('editAlert')
                        : t('title')}
                  </Text>
                  {showCounter && (
                    <Text as="span" variant="body-small" className="text-v2-text-secondary">
                      ({activeAlertsCount}/{ACTIVE_ALERTS_MAX})
                    </Text>
                  )}
                </Text>
              </DialogPrimitive.Title>
              <SheetIconButton onClick={handleClose} ariaLabel={tCommon('close')}>
                <Icon name="Close" size={24} />
              </SheetIconButton>
            </div>
            {(view === 'create' || view === 'edit') && (
              <div className="h-px w-full bg-v2-border" aria-hidden />
            )}
          </div>

          {view === 'list' && (
            <PriceAlertsListView
              alerts={alertsQuery.data ?? []}
              isLoading={alertsQuery.isLoading}
              isError={alertsQuery.isError}
              isUpdating={isListUpdating}
              onOpenCreate={handleOpenCreate}
              onEdit={handleOpenEdit}
            />
          )}
          {view === 'create' && (
            <PriceAlertsCreateView
              eventId={market.id}
              marketTitle={market.title}
              outcomes={outcomes}
              defaultOutcomeId={defaultOutcomeId}
              onCancel={handleBackToList}
              onCreate={handleCreate}
              isSubmitting={createMutation.isPending}
            />
          )}
          {view === 'edit' && editingAlert && (
            <PriceAlertEditView
              alert={editingAlert}
              onCancel={handleBackToList}
              onSave={handleEditSave}
              isSubmitting={updateMutation.isPending}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface SheetIconButtonProps {
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}

function SheetIconButton({ onClick, ariaLabel, children }: SheetIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] text-v2-icon-primary transition-colors hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover"
    >
      {children}
    </button>
  );
}
